import {Observable, throwError} from 'rxjs';
import {catchError, filter, map, takeUntil} from 'rxjs/operators';
import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {select, Store} from '@ngrx/store';

import {environment} from '@env/environment';
import {InstitutionsVendorListModel, VendorInstitutionsReportItem} from '@app/core/model/institutional-model';
import {isAuthenticated, State} from '@app/app.reducers';


@Injectable()
export class InstitutionsService {

    public errorInstitutions = new EventEmitter <string> ();

    constructor(private http: HttpClient,
                private store: Store<State>) {
    }

    getInstitutions(appId: string): Observable<InstitutionsVendorListModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/vendor/${appId}/institutions`;

        return this.http.get<InstitutionsVendorListModel[]>(url)
            .pipe(
                map((institutions: InstitutionsVendorListModel[]) => {
                    institutions.sort(
                        (inst1: InstitutionsVendorListModel, inst2: InstitutionsVendorListModel) =>
                            inst1.name.localeCompare(inst2.name));

                    return institutions;
                }),
                catchError(this.handleError('Could not load institutions.')),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getVendorInstitutionsForAllApplications(vendorGuid: string): Observable<VendorInstitutionsReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/institutions/vendor/${vendorGuid}`;

        return this.http.get<VendorInstitutionsReportItem[]>(url)
            .pipe(
                map((vendorInstitutionsReportItems: VendorInstitutionsReportItem[]) => vendorInstitutionsReportItems),
                catchError(this.handleError('Could not load institutions.')),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(authenticated => !authenticated)))
            );
    }

    getInstitutionsLazyLoading(appId: string): Observable<InstitutionsVendorListModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/vendor/${appId}/institutions-lazy`;

        return this.http.get<InstitutionsVendorListModel[]>(url)
            .pipe(
                map((institutions: InstitutionsVendorListModel[]) => {
                    institutions.sort(
                        (inst1: InstitutionsVendorListModel, inst2: InstitutionsVendorListModel) =>
                            inst1.name.localeCompare(inst2.name));

                    return institutions;
                }),
                catchError(this.handleError('Could not load lazy institutions data.')),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }


    getInstitutionsNeedsVendorDecision(vendorAppGuid: string): Observable<string[]> {
        const url = `${environment.datahubBaseURI}/api/v2/significant-changes/districts-needs-vendor-decision?vendorAppGuid=${vendorAppGuid}`;

        return this.http.get<string[]>(url)
            .pipe(
                catchError(this.handleError('Could not load institutions needs vendor decision.')),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getIntegrationsWithActivatedBanner(vendorAppGuid: string): Observable<string[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/${vendorAppGuid}/integrations/trial`;

        return this.http.get<string[]>(url)
            .pipe(
                catchError(this.handleError('Could not load list of integrations with promo banner.')),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    private handleError(operation: string) {
        return (err: any) => {
            const errMsg = `error in ${operation}()`;
            if (err instanceof HttpErrorResponse) {
                console.log(`status: ${err.status}, ${err.statusText}`);
            } else {
                console.log(`${errMsg}:`, err);
            }
            this.errorInstitutions.emit ('Something bad happened, please try again later.');
            return throwError(errMsg);
        };
    }
}

